import { Injectable } from '@angular/core';
import { Conditions } from '@vsolv/packages/conditions/domain';
import { Policy } from '@wsphere/warranties/domain';
import {
  DescriptiveColumnConfig,
  getAvailableColumnKeys,
  getColumnConfig,
} from '../components/new-descriptive-columns-button/descriptive-columns.config';

// Type-safe descriptive field keys
type DescriptiveFieldKey = 'sku' | 'skuDescription';

// Type for descriptive fields in coverage config
type DescriptiveFields = {
  sku?: Conditions.Rule<string>;
  skuDescription?: Conditions.Rule<string>;
};

@Injectable({
  providedIn: 'root',
})
export class DescriptiveColumnsService {
  /**
   * Get all available column configurations
   */
  getAvailableColumns(): DescriptiveColumnConfig[] {
    return getAvailableColumnKeys()
      .map(key => getColumnConfig(key))
      .filter(Boolean) as DescriptiveColumnConfig[];
  }

  /**
   * Check which columns have data for a given term
   */
  getColumnsWithData(coverageConfigs: Policy.Plan.CoverageConfig[]): string[] {
    const columnsWithData: string[] = [];

    for (const columnKey of getAvailableColumnKeys()) {
      const hasData = coverageConfigs.some(config => {
        const field = this.getColumnField(config, columnKey);
        return field?.defaultValue || (field?.blocks && field.blocks.length > 0);
      });

      if (hasData) {
        columnsWithData.push(columnKey);
      }
    }

    return columnsWithData;
  }

  /**
   * Create a default rule with empty string value
   */
  private createDefaultStringRule(): Conditions.Rule<string> {
    return {
      defaultValue: '',
      blocks: [],
    } as Conditions.Rule<string>;
  }

  /**
   * Initialize column fields for coverage configs
   */
  initializeColumnFields(coverageConfigs: Policy.Plan.CoverageConfig[], columnKeys: string[]): void {
    coverageConfigs.forEach(config => {
      columnKeys.forEach(columnKey => {
        if (!this.getColumnField(config, columnKey)) {
          this.setColumnField(config, columnKey, this.createDefaultStringRule());
        }
      });
    });
  }

  /**
   * Clear data for specific columns
   */
  clearColumnData(coverageConfigs: Policy.Plan.CoverageConfig[], columnKey: string): void {
    coverageConfigs.forEach(config => {
      this.setColumnField(config, columnKey, undefined);
    });
  }

  /**
   * Update a specific column field for a coverage config
   */
  updateColumnField(
    coverageConfig: Policy.Plan.CoverageConfig,
    columnKey: string,
    rule: Conditions.Rule<string> | null
  ): void {
    this.setColumnField(coverageConfig, columnKey, rule);
  }

  /**
   * Get column field value from coverage config (type-safe)
   */
  getColumnField(config: Policy.Plan.CoverageConfig, columnKey: string): Conditions.Rule<string> | undefined {
    // Type-safe access using proper type guard and interface
    if (this.isDescriptiveField(columnKey)) {
      return (config as DescriptiveFields)[columnKey];
    }
    return undefined;
  }

  /**
   * Set column field value in coverage config (type-safe)
   */
  private setColumnField(
    config: Policy.Plan.CoverageConfig,
    columnKey: string,
    value: Conditions.Rule<string> | null | undefined
  ): void {
    // Type-safe setting using proper type guard and interface
    if (this.isDescriptiveField(columnKey)) {
      // Type-safe property assignment using the DescriptiveFields interface
      (config as DescriptiveFields)[columnKey] = value === null ? undefined : value;
    } else {
      console.warn(`Unknown column key: ${columnKey}`);
    }
  }

  /**
   * Type guard to check if a string is a valid descriptive field key
   */
  private isDescriptiveField(key: string): key is DescriptiveFieldKey {
    return ['sku', 'skuDescription'].includes(key);
  }

  /**
   * Get column configuration by key
   */
  getColumnConfig(columnKey: string): DescriptiveColumnConfig | undefined {
    return getColumnConfig(columnKey);
  }

  /**
   * Validate column value based on its configuration
   */
  validateColumnValue(columnKey: string, value: string | null | undefined): boolean {
    const config = getColumnConfig(columnKey);
    if (!config) return false;

    if (config.required && (!value || value === '')) {
      return false;
    }

    if (config.maxLength && typeof value === 'string' && value.length > config.maxLength) {
      return false;
    }

    return true;
  }
}
